//
// Copyright (c) 2011-2024 by Curalate, Inc.
//
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  FlatDefaultButton,
  FlatPrimaryButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import CalendarView from '@components/date-range-picker/CalendarView';
import CustomRangeDisplay from '@components/date-range-picker/CustomRangeDisplay';
import { DateRangePickerContextProvider } from '@components/date-range-picker/DateRangePickerContext';
import {
  DateRangeAliasDisplayNames,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { useDateRangeSelectionModel } from '@components/date-range-picker/useDateRangeSelectionModel';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { isDefined } from '@utils/filterPredicates';

interface DateRangePickerPanelProps {
  aliases?: IAliasedDateRange[];
  className?: string;
  footnote?: React.ReactNode;
  initialValue: IAliasedDateRange;
  isOpen: boolean;
  maxDate?: DateString;
  maxDateRange?: number;
  minDate?: DateString;
  onCancelClicked?: () => void;
  onSetDateClicked?: (range: IAliasedDateRange) => void;
  timeZone: TimeZoneId;
  isInitialEndDateDisabled?: boolean;
}

interface IAliasButtonProps
  extends React.ComponentProps<
    typeof FlatPrimaryButton | typeof FlatDefaultButton
  > {
  isActiveAlias: boolean;
}
/**
 * Use different styles of button depending on whether the alias is active or not.
 */
const AliasButton = (props: IAliasButtonProps) => {
  const { isActiveAlias, ...restProps } = props;
  return isActiveAlias ? (
    <FlatPrimaryButton {...restProps} />
  ) : (
    <FlatDefaultButton {...restProps} />
  );
};

const DateRangePickerPanel = ({
  aliases = [],
  footnote,
  initialValue,
  isInitialEndDateDisabled = false,
  isOpen,
  maxDate,
  maxDateRange,
  minDate,
  onCancelClicked = () => undefined,
  onSetDateClicked = () => undefined,
  timeZone,
}: DateRangePickerPanelProps) => {
  const selection = useDateRangeSelectionModel(
    initialValue.startDate,
    initialValue.endDate,
    initialValue.displayName,
    minDate,
    maxDate,
    maxDateRange,
  );

  const hasFullRangeSelected =
    isDefined(selection.startDate) && isDefined(selection.endDate);

  const isCustomRange = ((a) => !a || a === 'Custom')(selection.aliasKey);
  const hasInvalidCustomRange =
    isCustomRange &&
    !(selection.customEndDate.isValid && selection.customStartDate.isValid);

  const lastNonCustomKeyRef = useRef('');
  const wasOpenRef = useRef(false);
  const prevAliasRef = useRef(selection.aliasKey);
  const keyRef = useRef(`${selection.aliasKey}-${Date.now()}`);

  // Generate new key only when alias changes
  if (prevAliasRef.current !== selection.aliasKey) {
    keyRef.current = `${selection.aliasKey}-${Date.now()}`;
    prevAliasRef.current = selection.aliasKey;
  }

  if (!isCustomRange) {
    lastNonCustomKeyRef.current = keyRef.current;
  }

  const justOpened = isOpen && !wasOpenRef.current;
  wasOpenRef.current = isOpen;
  // The calendarKey is used to force the CalendarView component to re-render when necessary.
  // When a custom range is selected and the panel was not just opened, we use the last non-custom key
  // to prevent unnecessary re-renders and preserve the calendar state. Otherwise, we use the current key,
  // which updates when the alias changes or the panel is just opened.
  const calendarKey =
    isCustomRange && !justOpened ? lastNonCustomKeyRef.current : keyRef.current;

  const initialDate = () => {
    if (selection.aliasKey === DateRangeAliasDisplayNames.allTime) {
      return DateString.getToday(timeZone);
    }
    return isDefined(selection.endDate) && !isInitialEndDateDisabled
      ? DateString.addMonths(selection.endDate, -1)
      : selection.startDate ?? DateString.getToday(timeZone);
  };

  const handleCancel = () => {
    onCancelClicked();
  };

  useEffect(() => {
    if (!isOpen) {
      selection.setRange(initialValue);
    }
  }, [isOpen]);

  return (
    <DateRangePickerContextProvider
      value={{ minDate, maxDate, maxDateRange, ...selection }}
    >
      <Panel>
        <Body>
          <CalendarContainer>
            <CalendarView
              key={calendarKey}
              initialDate={DateString.applyMomentSetter(
                initialDate(),
                'date',
                1,
              )}
            ></CalendarView>
          </CalendarContainer>
          <AliasContainer>
            {aliases.map((alias) => (
              <AliasButton
                key={alias.displayName}
                isActiveAlias={selection.aliasKey === alias.displayName}
                onClick={() => selection.setRange(alias)}
              >
                <AliasButtonText>{alias.displayName}</AliasButtonText>
              </AliasButton>
            ))}
            <AliasButton
              key='Custom'
              isActiveAlias={isCustomRange}
              onClick={() => selection.swapToCustomRange()}
            >
              <AliasButtonText>Custom</AliasButtonText>
            </AliasButton>
            {isCustomRange && (
              <RangeDisplayContainer>
                <RangeInputs />
              </RangeDisplayContainer>
            )}
          </AliasContainer>
        </Body>
        <Footer>
          <div>
            {
              // If footnote requires date picker panel state information, we could:
              // A. Use a render props pattern
              // OR
              // B. Use the connect/monitor pattern to move ownership of the state system to the consumer.
              footnote
            }
          </div>
          <div>
            <StyledFlatPrimaryButton onClick={handleCancel}>
              Cancel
            </StyledFlatPrimaryButton>
            <PrimaryButton
              isDisabled={!hasFullRangeSelected || hasInvalidCustomRange}
              onClick={() =>
                selection.startDate &&
                selection.endDate &&
                onSetDateClicked({
                  displayName: selection.aliasKey ?? 'Custom',
                  startDate: selection.startDate,
                  endDate: selection.endDate,
                  timeZone,
                })
              }
            >
              Set Date
            </PrimaryButton>
          </div>
        </Footer>
      </Panel>
    </DateRangePickerContextProvider>
  );
};

const Panel = styled.div(
  ({ theme }) => `
    display: inline-block;
`,
);

const Body = styled.div(
  ({ theme }) => `
    display: inline-flex;
`,
);

const Footer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacings[4]};
    border-top: 1px solid ${theme.colors.gray2};
`,
);
const AliasContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    padding: ${theme.spacings[3]};
    padding-right: ${theme.spacings[4]};
    border-left: 1px solid ${theme.colors.gray2};
`,
);

const AliasButtonText = styled.div(
  ({ theme }) => `
    // Same arbitrary width from the old date range picker
    width: 160px;
    text-align: left;
`,
);

const RangeDisplayContainer = styled.div(
  ({ theme }) => `
  padding-left: ${theme.spacings[3]};
  padding-top: ${theme.spacings[2]};
`,
);
const RangeInputs = styled(CustomRangeDisplay)(
  ({ theme }) => `
  // Same arbitrary width from the old date range picker
  width: 176px;
`,
);

const CalendarContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[4]};
`,
);

const StyledFlatPrimaryButton = styled(FlatPrimaryButton)(
  ({ theme }) => `
  margin-right: ${theme.spacings[1]};
`,
);

export default DateRangePickerPanel;
